import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { isApiError } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { useMyOrg } from "@/hooks/use-my-org";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import {
  compareFacilityPrograms,
  type ProgramInsightsResponse,
} from "@/libs/analyticsApi";

const completedFourWeekWindow = () => {
  const currentWeek = new Date();
  currentWeek.setUTCHours(0, 0, 0, 0);
  const daysSinceMonday = (currentWeek.getUTCDay() + 6) % 7;
  currentWeek.setUTCDate(currentWeek.getUTCDate() - daysSinceMonday);
  const lastDay = new Date(currentWeek);
  lastDay.setUTCDate(lastDay.getUTCDate() - 1);
  const firstDay = new Date(currentWeek);
  firstDay.setUTCDate(firstDay.getUTCDate() - 28);
  return {
    start: firstDay.toISOString().slice(0, 10),
    end: lastDay.toISOString().slice(0, 10),
  };
};
const asInstant = (date: string) => `${date}T00:00:00Z`;
const afterInclusiveDate = (date: string) => {
  const end = new Date(asInstant(date));
  end.setUTCDate(end.getUTCDate() + 1);
  return end.toISOString();
};
const dateProps = {
  fullWidth: true,
  size: "small" as const,
  slotProps: { inputLabel: { shrink: true } },
  type: "date",
};
const humanize = (value: string) =>
  value.replace(/_/g, " ").replace(/^./, (letter) => letter.toUpperCase());

function CountMetric({ label, value }: { label: string; value: number }) {
  return (
    <Box>
      <Typography
        sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 800 }}
        variant="h6"
      >
        {new Intl.NumberFormat().format(value)}
      </Typography>
      <Typography color="text.secondary" variant="caption">
        {label}
      </Typography>
    </Box>
  );
}

function ProgramCard({
  program,
}: {
  program: ProgramInsightsResponse["programs"][number];
}) {
  return (
    <Paper component="article" variant="outlined" sx={{ p: 2.5 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
        >
          <Box>
            <Typography component="h2" sx={{ fontWeight: 800 }} variant="h6">
              {program.program_name}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {humanize(program.program_status)}
            </Typography>
          </Box>
          <Chip
            color={program.absent_records > 0 ? "warning" : "success"}
            label={`${program.attendance_percent}% marked attendance`}
            size="small"
            variant="outlined"
          />
        </Stack>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          }}
        >
          <CountMetric label="Present" value={program.present_records} />
          <CountMetric label="Late" value={program.late_records} />
          <CountMetric label="Absent" value={program.absent_records} />
        </Box>
        <Typography color="text.secondary" variant="body2">
          Denominator: {program.marked_attendance_records} marked records across{" "}
          {program.marked_occurrences} occurrence
          {program.marked_occurrences === 1 ? "" : "s"}. Unmarked roster places
          are not included.
        </Typography>
        <Divider />
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)" },
          }}
        >
          <CountMetric label="Delivered" value={program.completed_sessions} />
          <CountMetric label="Partial" value={program.partial_sessions} />
          <CountMetric
            label="Coach no-show"
            value={program.coach_no_show_sessions}
          />
          <CountMetric label="Cancelled" value={program.cancelled_sessions} />
        </Box>
      </Stack>
    </Paper>
  );
}

export function ProgramInsightsWorkspace() {
  const { user } = useAuth();
  const orgView = useMyOrg();
  const {
    facility,
    facilityId,
    isLoading: facilityLoading,
  } = useResolvedFacility();
  const initialWindow = useRef(completedFourWeekWindow()).current;
  const [start, setStart] = useState(initialWindow.start);
  const [end, setEnd] = useState(initialWindow.end);
  const [result, setResult] = useState<ProgramInsightsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<
    "unavailable" | "forbidden" | "invalid" | "retry" | null
  >(null);
  const generation = useRef(0);
  const activeFacilityId = useRef(facilityId);
  activeFacilityId.current = facilityId;
  const organizationOwner = Boolean(
    user?.uid && orgView.org?.owner_user_id === user.uid,
  );

  useEffect(() => {
    generation.current += 1;
    setResult(null);
    setError(null);
    setLoading(false);
  }, [facilityId]);

  const load = async () => {
    if (!facilityId || loading) return;
    const requestFacilityId = facilityId;
    const requestGeneration = ++generation.current;
    const current = () =>
      generation.current === requestGeneration &&
      activeFacilityId.current === requestFacilityId;
    setLoading(true);
    setError(null);
    try {
      const response = await compareFacilityPrograms(requestFacilityId, {
        start: asInstant(start),
        end: afterInclusiveDate(end),
      });
      if (!current()) return;
      if (response.facility_id !== requestFacilityId) {
        setResult(null);
        setError("retry");
        return;
      }
      setResult(response);
    } catch (cause) {
      if (!current()) return;
      setResult(null);
      if (isApiError(cause) && cause.status === 503) setError("unavailable");
      else if (isApiError(cause) && cause.status === 403) setError("forbidden");
      else if (isApiError(cause) && cause.status === 422) setError("invalid");
      else setError("retry");
    } finally {
      if (current()) setLoading(false);
    }
  };

  if (facilityLoading || !facilityId) {
    return (
      <Stack
        aria-label="Loading Program insights"
        role="status"
        spacing={1}
        sx={{
          alignItems: "center",
          justifyContent: "center",
          minHeight: "40vh",
        }}
      >
        <CircularProgress aria-hidden size={24} />
        <Typography color="text.secondary" variant="body2">
          Loading…
        </Typography>
      </Stack>
    );
  }

  const messages = {
    unavailable:
      "Program insights aren’t enabled for this organization. Programs, rosters, attendance, receipts, and exports remain available.",
    forbidden: "You don’t have permission to view internal Program insights.",
    invalid:
      "Choose complete UTC weeks: start on Monday, end on Sunday, up to 13 weeks.",
    retry:
      "We couldn’t build this Program report. Review the dates and try again.",
  };

  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
      >
        <Box>
          <Typography component="h1" sx={{ fontWeight: 800 }} variant="h5">
            Program attendance &amp; delivery
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Review marked attendance and certified delivery exceptions at{" "}
            {facility?.name ?? "this facility"}.
          </Typography>
        </Box>
        <Chip
          color="primary"
          icon={<GroupsOutlinedIcon />}
          label="Insights module"
          variant="outlined"
        />
      </Stack>

      <Paper
        component="form"
        onSubmit={(event) => {
          event.preventDefault();
          void load();
        }}
        sx={{ p: { xs: 2, sm: 2.5 } }}
        variant="outlined"
      >
        <Stack spacing={2}>
          <Box>
            <Typography component="h2" sx={{ fontWeight: 700 }} variant="h6">
              Select a completed period
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Select complete UTC weeks, Monday through Sunday, up to 13 weeks.
              The report uses marked attendance only and never exposes learner
              identities.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              {...dateProps}
              label="Start date"
              onChange={(event) => setStart(event.target.value)}
              value={start}
            />
            <TextField
              {...dateProps}
              label="Last day"
              onChange={(event) => setEnd(event.target.value)}
              value={end}
            />
          </Stack>
          <Button
            disabled={loading}
            sx={{ alignSelf: "flex-start" }}
            type="submit"
            variant="contained"
          >
            {loading ? "Building report…" : "Build Program report"}
          </Button>
        </Stack>
      </Paper>

      {error ? (
        <Alert
          action={
            error === "unavailable" && organizationOwner ? (
              <Button
                color="inherit"
                component={RouterLink}
                size="small"
                to="/settings/contract"
              >
                Review modules
              </Button>
            ) : error === "retry" ? (
              <Button color="inherit" onClick={() => void load()} size="small">
                Retry
              </Button>
            ) : undefined
          }
          severity={error === "unavailable" ? "info" : "error"}
        >
          {messages[error]}
        </Alert>
      ) : null}

      {result?.facility_id === facilityId ? (
        <Stack spacing={2}>
          <Paper component="section" sx={{ p: 2.5 }} variant="outlined">
            <Stack spacing={1.5}>
              <Box>
                <Typography
                  component="h2"
                  sx={{ fontWeight: 800 }}
                  variant="h6"
                >
                  Included cohort
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  {result.decision_supported}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(4, minmax(0, 1fr))",
                  },
                }}
              >
                <CountMetric
                  label="Marked records"
                  value={result.totals.marked_attendance_records}
                />
                <CountMetric
                  label="Present"
                  value={result.totals.present_records}
                />
                <CountMetric label="Late" value={result.totals.late_records} />
                <CountMetric
                  label="Absent"
                  value={result.totals.absent_records}
                />
              </Box>
            </Stack>
          </Paper>

          <Alert severity="info">
            Programs appear only when every included UTC week has at least{" "}
            {result.cohort_minimum} distinct learners with marked attendance.
            Other Programs are hidden and excluded from totals.
          </Alert>

          {result.programs.length ? (
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "1fr",
                  xl: "repeat(2, minmax(0, 1fr))",
                },
              }}
            >
              {result.programs.map((program) => (
                <ProgramCard key={program.program_id} program={program} />
              ))}
            </Box>
          ) : (
            <Paper sx={{ p: 3, textAlign: "center" }} variant="outlined">
              <Typography sx={{ fontWeight: 700 }}>
                No reportable Program cohort
              </Typography>
              <Typography color="text.secondary" variant="body2">
                No Program met the weekly privacy cohort of{" "}
                {result.cohort_minimum} distinct learners in this period.
              </Typography>
            </Paper>
          )}

          <Paper component="section" sx={{ p: 2.5 }} variant="outlined">
            <Typography component="h2" sx={{ fontWeight: 800 }} variant="h6">
              How to read this report
            </Typography>
            <Stack component="ul" spacing={0.75} sx={{ mb: 0, mt: 1, pl: 2.5 }}>
              {result.limitations.map((limitation) => (
                <Typography component="li" key={limitation} variant="body2">
                  {limitation}
                </Typography>
              ))}
            </Stack>
          </Paper>
        </Stack>
      ) : null}
    </Stack>
  );
}
